import { useEffect, useState, useRef } from 'react';
import PrimaryButton from '../components/PrimaryButton';
import { Checkbox, Loader, TextInput } from '@mantine/core'; // Remove Radio import
import MultiStepFormLayout from '../Layout/FormLayout';
import { useForm } from '@mantine/form';
import {
  calculateAge,
  calculateLHC,
  formatTypeOfCover,
  pushDataLayer,
  refactorCoverIsFor,
  sanitizedUtmData,
  toFullName,
} from '../utils/helpers';
import { CoverIsFor } from '../types/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  decrementStep,
  setCurrentStep,
  setLeadRefNumber,
  updateFormData,
  updateSalesReqBody,
} from '../features/FormRegistration';
import axiosRequest from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { getUtmInfo } from '../utils/form/getUtmInfo';
import { getTierBasedOnIndex } from '../utils/form/rebateCalculator';
import SecondaryButton from '../components/SecondaryButton';
import LeftChevron from '../icons/LeftChevron';
import { transferName } from '../utils/form/helpers';
import { validatorRegexes } from '../utils/form/validator';
import { trackLead } from '../components/MetaPixel';

function Step6() {
  const { formData, salesReqBody, utmData } = useSelector(
    (state: any) => state.formRegistration,
  );

  // Initialize form with Redux state instead of hardcoded values
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      fullName: toFullName(salesReqBody.customer),
      phone: salesReqBody.customer.phone || '',
      email: salesReqBody.customer.email,
      hasHealthFund:
        typeof formData.hasHealthFund === 'undefined'
          ? 'Yes'
          : (formData.hasHealthFund === true || formData.hasHealthFund === 'Yes'
            ? 'Yes'
            : 'No'),
      healthFund: formData.healthFund,
      // Keep these for internal state management but don't show UI
      continuousCoverage: formData.hadContinuousCover !== undefined 
        ? (formData.hadContinuousCover ? 'Yes' : 'No') 
        : 'Yes',
      partnerContinuousCoverage: formData.hadContinuousCoverPartner !== undefined 
        ? (formData.hadContinuousCoverPartner ? 'Yes' : 'No') 
        : 'Yes',
      acceptedPrivacyPolicy: false,
      partnerSameHealthFund: 'Yes',
      partnerHealthFund: formData.partnerHealthFund,
      customerLoading: formData.customerLoading || 0,
      partnerLoading: formData.partnerLoading || 0,
      previousPayment: formData.previousPayment || '',
    },

    validate: {
      fullName: (value) => {
        if (!value) return 'Name is required';
        let result = null;
        if (value) {
          const nameComponent = (value as string).split(' ');
          if (nameComponent.length < 2) {
            return 'Name should contain at least 2 words';
          }
          if (nameComponent.length > 3) {
            return 'Name should not contain more than 3 words';
          }
          nameComponent.forEach((element) => {
            if (!validatorRegexes.name.test(element)) {
              result = 'Name should not contain special characters';
              return;
            }
          });
        }
        return result;
      },

      phone: (value) => {
        if (!value) return 'Phone number is required';
        if (value) {
          if (!/^04\d{8}$/.test(value))
            return 'Phone number must start with 04 and have 10 digits';
        }
        return null;
      },

      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+\.\S+$/.test(value)) return 'Invalid email address';
        return null;
      },

      acceptedPrivacyPolicy: (value) =>
        value ? null : 'Please accept the privacy policy',

      previousPayment: (value) => {
        if (isMobileView) return null;
        if (formData.hasHealthFund === 'No') return null;
        if (!value) {
          return null;
        }
        if (!/^\d+(\.\d{1,2})?$/.test(value))
          return 'Please enter a valid amount';
        return null;
      },
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submissionAttempts, setSubmissionAttempts] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clickCount, setClickCount] = useState(0); // Add immediate button disable in Step6.tsx
  const lastSubmissionRef = useRef<string>('');

  const { values, getInputProps } = form;

  const { isMobileView } = useSelector((state: any) => state.viewport);
  const dispatch = useDispatch();

  const customerAge = calculateAge(formData.dateOfBirth?.[0]);
  const partnerAge = calculateAge(formData?.dateOfBirth?.[1]);

  const generateSubmissionKey = () => {
    return `${form.values.email}-${form.values.phone}-${Date.now()}`;
  };

  const handleLeadRequest = async () => {
    const submissionKey = generateSubmissionKey();
    
    if (lastSubmissionRef.current === submissionKey.split('-').slice(0, 2).join('-')) {
      
      return;
    }
    
    lastSubmissionRef.current = submissionKey.split('-').slice(0, 2).join('-');
    setClickCount(prev => prev + 1);
    
    if (isSubmitting || isLoading || clickCount > 0) {
      
      return;
    }

    setIsSubmitting(true);
    setIsLoading(true);
  
    try {
      // Add duplicate detection logging
      const hasPartner = formData.coverIsFor === CoverIsFor.Couple || 
                        formData.coverIsFor === CoverIsFor.Family;

      // ✅ FIX: Determine continuous coverage based on health fund status
      const shouldHaveContinuousCover = () => {
        // If they have no health fund, they don't have continuous cover
        if (formData.hasHealthFund === 'No' || formData.hasHealthFund === false) {
          return false;
        }
        
        // If they have a health fund, use the stored value or default based on age
        if (formData.hadContinuousCover !== undefined) {
          return formData.hadContinuousCover;
        }
        
        // Default: Young people (under 30) likely have continuous cover
        return customerAge >= 18 && customerAge <= 30;
      };

      const shouldPartnerHaveContinuousCover = () => {
        // ✅ FIX: Check PARTNER's health fund status, not primary person's
        // First check if partner has same fund as primary
        if (formData.partnerSameHealthFund === 'Yes' || formData.partnerSameHealthFund === true) {
          // Partner uses same fund as primary person
          return shouldHaveContinuousCover();
        }
        
        // ✅ FIX: Check partner-specific health fund status
        if (formData.partnerSameHealthFund === 'No Fund' || 
            formData.partnerHealthFund === 'No Fund' || 
            formData.partnerHealthFund === null ||
            formData.partnerHealthFund === undefined) {
          return false; // Partner has no health fund = no continuous cover
        }
        
        // If partner has their own health fund, use the stored value or default based on age
        if (formData.hadContinuousCoverPartner !== undefined) {
          return formData.hadContinuousCoverPartner;
        }
        
        // Default: Young people (under 30) likely have continuous cover
        return partnerAge >= 18 && partnerAge <= 30;
      };

      const hospitalBenefits = formData.selectedHospitalBenefits.map(
        (benefit_id: any) => ({ id: benefit_id }),
      );

      const extraBenefits = formData.selectedExtrasBenefits.map(
        (benefit_id: any) => ({ id: benefit_id }),
      );

      axiosRequest
        .post('/leads', {
          general_details: {
            customer_type: refactorCoverIsFor(formData.coverIsFor),
            product_type: formatTypeOfCover(formData.typeOfCover),
            hospital_benefits: hospitalBenefits,
            extras_benefits: extraBenefits,
          },
          customer: {
            ...transferName(values.fullName),
            dob: formData.dateOfBirth?.[0]
              .replaceAll('/', '-')
              .split('-')
              .reverse()
              .join('-'),
            // ✅ FIX: Use proper continuous cover logic
            continuous_cover: shouldHaveContinuousCover(),
            email: form.values.email,
            ...(form.values.phone && {
              phone: form.values.phone,
              mobile: form.values.phone,
            }),
            previous_fund_code: formData.healthFundCode,
            is_receive_marketing: form.values.acceptedPrivacyPolicy ? 1 : 2,
          },
          ...(hasPartner
            ? {
                partner: {
                  dob: formData.dateOfBirth?.[1]
                    .replaceAll('/', '-')
                    .split('-')
                    .reverse()
                    .join('-'),
                  // ✅ FIX: Use proper partner continuous cover logic
                  continuous_cover: shouldPartnerHaveContinuousCover(),
                },
              }
            : {}),
          ...(formData.coverIsFor === CoverIsFor.Family ||
          formData.coverIsFor === CoverIsFor.SingleParentFamily
            ? {
                dependants: salesReqBody.dependants.map((dependant: any) => ({
                  age: dependant.age,
                  education_details: {
                    full_time: dependant.education_details.full_time,
                  },
                })),
              }
            : {}),
          residential_address: {
            state: formData.state || 1,
          },
          government_rebate: {
            income_tier: formData.tierIndex === 4 ? 3 : formData.tierIndex,
          },
          marketing: {
            ...sanitizedUtmData(getUtmInfo()),
            ip_address: utmData?.ip_address || '',
          },
        })
        .then((res) => {
          const leadReferenceId = res.data.data.reference_id;
          dispatch(setLeadRefNumber(leadReferenceId));
          dispatch(
            updateSalesReqBody({
              ...salesReqBody,
              general_details: {
                ...salesReqBody.general_details,
                lead_id: leadReferenceId,
              },
              customer: {
                ...salesReqBody.customer,
                ...transferName(values.fullName),
                agr_answer_1: getTierBasedOnIndex(formData.tierIndex),
              },
              government_rebate: {
                ...salesReqBody.government_rebate,
                income_tier: formData.tierIndex === 4 ? 3 : formData.tierIndex,
                rebate_percentage: formData.rebatePercentage,
                claiming_rebate: formData.tierIndex !== 4,
              },
            }),
          );
          dispatch(setCurrentStep(9));
        })
        .catch((err) => {
          if (err.status === 422) {
            toast.error('Form validation failed. Please check your details.');
          } else {
            toast.error('An error occurred. Please try again later.');
          }
        
        })
        .finally(() => {
          setIsSubmitting(false);
          setIsLoading(false);
        });
    } catch (error) {
      
      toast.error('An unexpected error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
      setClickCount(0); // Reset after completion
    }
  };
  // ✅ REMOVE: Delete all three existing useEffect hooks and replace with this single one
  useEffect(() => {
    const hasPartner = formData.coverIsFor === CoverIsFor.Couple || 
                      formData.coverIsFor === CoverIsFor.Family;

  console.log('🔍 LHC CALCULATION DEBUG:', {
    hasHealthFund: formData.hasHealthFund,
    customerAge: customerAge,
    partnerAge: partnerAge,
    partnerSameHealthFund: formData.partnerSameHealthFund, // ✅ This is showing undefined
    partnerSameHealthFundType: typeof formData.partnerSameHealthFund, // ✅ Add this
    hasPartner: hasPartner,
    currentCustomerLoading: formData.customerLoading,
    currentPartnerLoading: formData.partnerLoading,
    // ✅ ADD: Show entire formData to see what's stored
    fullFormData: formData
    });

    let updatedFormData = { ...formData };

    // ✅ Handle PRIMARY PERSON
    if (formData.hasHealthFund === 'No' || formData.hasHealthFund === false) {
      // No health fund = calculate LHC loading
      const customerLHC = calculateLHC(formData.dateOfBirth?.[0]);
      console.log('💰 Customer has NO health fund - LHC:', customerLHC);
      
      updatedFormData = {
        ...updatedFormData,
        hadContinuousCover: false,
        customerLoading: customerLHC,
        changedLoadingManuallyCustomer: false,
      };
    } else if (formData.hasHealthFund === 'Yes' || formData.hasHealthFund === true) {
      // ✅ Has health fund = NO LHC loading (assuming continuous coverage)
      console.log('✅ Customer HAS health fund - NO LHC loading');
      
      updatedFormData = {
        ...updatedFormData,
        hadContinuousCover: true, // Assume continuous coverage if they have a fund
        customerLoading: 0, // NO LHC loading
        changedLoadingManuallyCustomer: false,
      };
    }

    // ✅ Handle PARTNER
  if (hasPartner) {
    if (formData.partnerSameHealthFund === 'Yes' || formData.partnerSameHealthFund === true) {
      // Partner has same fund as primary - follow primary's status
      if (formData.hasHealthFund === 'No' || formData.hasHealthFund === false) {
        const partnerLHC = calculateLHC(formData.dateOfBirth?.[1]);
        console.log('💰 Calculating partner LHC (same as primary):', partnerLHC);
        
        updatedFormData = {
          ...updatedFormData,
          hadContinuousCoverPartner: false,
          partnerLoading: partnerLHC,
          changedLoadingManuallyPartner: false,
        };
      } else {
        const shouldPartnerHaveContinuousCover = partnerAge >= 18 && partnerAge <= 30;
        updatedFormData = {
          ...updatedFormData,
          hadContinuousCoverPartner: shouldPartnerHaveContinuousCover,
          partnerLoading: shouldPartnerHaveContinuousCover ? 0 : calculateLHC(formData.dateOfBirth?.[1]),
          changedLoadingManuallyPartner: false,
        };
      }
    } else if (formData.partnerSameHealthFund === 'No Fund') {
      // Partner has no health fund = calculate LHC loading
      const partnerLHC = calculateLHC(formData.dateOfBirth?.[1]);
      console.log('💰 Calculating partner LHC (no fund):', partnerLHC);
      
      updatedFormData = {
        ...updatedFormData,
        hadContinuousCoverPartner: false,
        partnerLoading: partnerLHC,
        changedLoadingManuallyPartner: false,
      };
    } else if (formData.partnerSameHealthFund === 'No' && formData.partnerHealthFund && formData.partnerHealthFund !== 'No Fund') {
      // Partner has different health fund
      const shouldPartnerHaveContinuousCover = partnerAge >= 18 && partnerAge <= 30;
      updatedFormData = {
        ...updatedFormData,
        hadContinuousCoverPartner: shouldPartnerHaveContinuousCover,
        partnerLoading: shouldPartnerHaveContinuousCover ? 0 : calculateLHC(formData.dateOfBirth?.[1]),
        changedLoadingManuallyPartner: false,
      };
    }
  }

  // ✅ Only dispatch if something actually changed
  const hasChanges = 
    updatedFormData.hadContinuousCover !== formData.hadContinuousCover ||
    updatedFormData.hadContinuousCoverPartner !== formData.hadContinuousCoverPartner ||
    updatedFormData.customerLoading !== formData.customerLoading ||
    updatedFormData.partnerLoading !== formData.partnerLoading;

  if (hasChanges) {
    console.log('🔄 Updating form data with LHC calculations:', {
      customerLoading: updatedFormData.customerLoading,
      partnerLoading: updatedFormData.partnerLoading,
      hadContinuousCover: updatedFormData.hadContinuousCover,
      hadContinuousCoverPartner: updatedFormData.hadContinuousCoverPartner
    });
    
    dispatch(updateFormData(updatedFormData));
  }
}, [
  customerAge, 
  partnerAge, 
  formData.hasHealthFund, 
  formData.partnerSameHealthFund, 
  formData.partnerHealthFund,
  formData.dateOfBirth
]);

  if (isMobileView) {
    return (
      <MultiStepFormLayout>
        <form
          onSubmit={form.onSubmit((values) => {
            trackLead();
            pushDataLayer('funnel_thank_you', {
              thank_you: 'lead form submitted',
            });
            handleLeadRequest();
          })}>
          <div className="flex flex-col items-center h-full justify-between px-[5px] md:px-[30px] w-full md:w-[550px] ">
            <h1 className="text-[22px] text-start font-bold self-start">
              Fill in your details
            </h1>
            <span className="text-[12px] text-start">
              Please fill in the below as accurately as possible. The prices
              displayed on the next page can be altered by your information
              including Lifetime Health Cover Loading.
            </span>
            <div className="flex flex-col gap-[27px] mt-[30px] w-full">
              <TextInput
                label="Full name"
                placeholder="First & Last Name"
                className="w-full text-start"
                {...form.getInputProps('fullName')}
                onChange={(e) => {
                  const value = e.target.value.replace(/[0-9]/g, '');
                  if (value.length <= 50) {
                    form.setFieldValue('fullName', value);
                    dispatch(
                      updateSalesReqBody({
                        ...salesReqBody,
                        customer: {
                          ...salesReqBody.customer,
                          ...transferName(value),
                        },
                      }),
                    );
                  }
                }}
              />
              <TextInput
                label="Phone number"
                placeholder="Your best contact number eg. 0400 000 000"
                className="w-full text-start"
                {...form.getInputProps('phone')}
                onChange={(e) => {
                  const input = e.target.value;

                  if (
                    input.length <= 10 &&
                    (input === '' ||
                      input === '0' ||
                      (input[0] === '0' && ['4'].includes(input[1])))
                  ) {
                    form.setFieldValue('phone', e.target.value);
                    dispatch(
                      updateSalesReqBody({
                        ...salesReqBody,
                        customer: {
                          ...salesReqBody.customer,
                          phone: e.target.value,
                          mobile: e.target.value,
                        },
                      }),
                    );
                  }
                }}
              />
              <TextInput
                placeholder="Your email address"
                label="Email"
                className="w-full text-start"
                {...getInputProps('email')}
                value={values.email}
                onChange={(e) => {
                  form.setFieldValue('email', e.target.value);
                  dispatch(
                    updateSalesReqBody({
                      ...salesReqBody,
                      customer: {
                        ...salesReqBody.customer,
                        email: e.target.value,
                      },
                    }),
                  );
                }}
              />
              
              <Checkbox
                className="mt-2 text-start"
                type="checkbox"
                label={
                  <span className="text-[12px] !text-start">
                    I agree to the website's{' '}
                    <a
                      className="mr-[5px] underline text-[#17385D]"
                      href=" https://healthdeal.com.au/terms-and-conditions/"
                      target="_blank"
                      rel="noreferrer">
                      Terms
                    </a>
                    and{' '}
                    <a
                      className="underline text-[#17385D]"
                      href=" https://healthdeal.com.au/privacy-policy/"
                      target="_blank"
                      rel="noreferrer">
                      Privacy Policy
                    </a>
                    . I give permission for Health Deal to contact me by phone, SMS, or email about health insurance. I also consent to receive occasional marketing communications from Health Deal, Energy Deal, and other Packleader Group companies. I understand this may include contact even if I'm listed on the Do Not Call Register.
                  </span>
                }
                checked={values.acceptedPrivacyPolicy}
                {...form.getInputProps('acceptedPrivacyPolicy')}
                onChange={(event) => {
                  form.setFieldValue(
                    'acceptedPrivacyPolicy',
                    event.currentTarget.checked,
                  );
                }}
              />
            </div>

            <div className="w-full justify-between h-full flex flex-col  items-start text-start gap-[10px] sticky bottom-0 bg-[white] p-4 z-10 border-t border-[#D1D7DF] mt-6">
              <div className="w-full flex justify-between items-center ">
                <SecondaryButton
                  leftSection={<LeftChevron />}
                  onClick={() => {
                    dispatch(decrementStep());
                  }}>
                  Previous
                </SecondaryButton>
                {isLoading ? (
                  <Loader />
                ) : (
                  <PrimaryButton
                    style={{
                      alignSelf: 'center',
                      whiteSpace: 'nowrap',
                    }}
                    text="View Policies"
                    type="submit"
                    disabled={isLoading || isSubmitting} // Prevent double-submit
                    classNames="w-[50%] md:w-auto"
                  />
                )}
              </div>
            </div>
          </div>
        </form>
      </MultiStepFormLayout>
    );
  }

  return (
    <MultiStepFormLayout>
      <form
        onSubmit={form.onSubmit((values) => {
          trackLead();
          pushDataLayer('funnel_thank_you', {
            thank_you: 'lead form submitted',
          });
          handleLeadRequest();
        })}
        className="w-full">
        <div className="flex flex-col items-center h-full justify-between w-full ">
          <h1 className="text-[22px] text-start font-bold self-start">
            Fill in your details
          </h1>
          <span className="text-[12px] text-start">
            Please fill in the below as accurately as possible. The prices
            displayed on the next page can be altered by your information
            including Lifetime Health Cover Loading.
          </span>
          <div className="flex flex-col gap-[27px] mt-[30px] w-full">
            <TextInput
              label="Full name"
              placeholder="First & Last Name"
              className="w-full text-start"
              {...form.getInputProps('fullName')}
              onChange={(e) => {
                const value = e.target.value.replace(/[0-9]/g, '');
                if (value.length <= 50) {
                  form.setFieldValue('fullName', value);
                  dispatch(
                    updateSalesReqBody({
                      ...salesReqBody,
                      customer: {
                        ...salesReqBody.customer,
                        ...transferName(value),
                      },
                    }),
                  );
                }
              }}
            />
            <TextInput
              label="Phone number"
              placeholder="Your best contact number eg. 0400 000 000"
              className="w-full text-start"
              {...form.getInputProps('phone')}
              onChange={(e) => {
                const input = e.target.value;

                if (
                  input.length <= 10 &&
                  (input === '' ||
                    input === '0' ||
                    (input[0] === '0' && ['4'].includes(input[1])))
                ) {
                  form.setFieldValue('phone', e.target.value);
                  dispatch(
                    updateSalesReqBody({
                      ...salesReqBody,
                      customer: {
                        ...salesReqBody.customer,
                        phone: e.target.value,
                        mobile: e.target.value,
                      },
                    }),
                  );
                }
              }}
            />
            <TextInput
              placeholder="Your email address"
              label="Email"
              className="w-full text-start"
              {...getInputProps('email')}
              value={values.email}
              onChange={(e) => {
                form.setFieldValue('email', e.target.value);
                dispatch(
                  updateSalesReqBody({
                    ...salesReqBody,
                    customer: {
                      ...salesReqBody.customer,
                      email: e.target.value,
                    },
                  }),
                );
              }}
            />
            
            <Checkbox
              className="mt-2 text-start"
              type="checkbox"
              label={
                <span className="text-[12px] !text-start">
                  I agree to the website's{' '}
                  <a
                    className="mr-[5px] underline text-[#17385D]"
                    href=" https://healthdeal.com.au/terms-and-conditions/"
                    target="_blank"
                    rel="noreferrer">
                    Terms
                  </a>
                  and{' '}
                  <a
                    className="underline text-[#17385D]"
                    href=" https://healthdeal.com.au/privacy-policy/"
                    target="_blank"
                    rel="noreferrer">
                    Privacy Policy
                  </a>
                  . I give permission for Health Deal to contact me by phone, SMS, or email about health insurance. I also consent to receive occasional marketing communications from Health Deal, Energy Deal, and other Packleader Group companies. I understand this may include contact even if I'm listed on the Do Not Call Register.
                </span>
              }
              checked={values.acceptedPrivacyPolicy}
              {...form.getInputProps('acceptedPrivacyPolicy')}
              onChange={(event) => {
                form.setFieldValue(
                  'acceptedPrivacyPolicy',
                  event.currentTarget.checked,
                );
              }}
            />
          </div>

          <div className="w-full justify-between h-full flex flex-col  items-start text-start gap-[10px] sticky bottom-0 bg-[white] p-4 z-10 border-t border-[#D1D7DF] mt-6">
            <div className="w-full flex justify-between items-center ">
              <SecondaryButton
                leftSection={<LeftChevron />}
                onClick={() => {
                  dispatch(decrementStep());
                }}>
                Previous
              </SecondaryButton>
              {isLoading ? (
                <Loader />
              ) : (
                <PrimaryButton
                  style={{
                    alignSelf: 'center',
                    whiteSpace: 'nowrap',
                  }}
                  text="View Policies"
                  type="submit"
                  disabled={isLoading || isSubmitting} // Prevent double-submit
                  classNames="w-[50%] md:w-auto"
                />
              )}
            </div>
          </div>
        </div>
      </form>
    </MultiStepFormLayout>
  );
}

export default Step6;
